import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import TaskReport from '../Tasks/TaskReport';
import AssistantManagerTasks from '../Tasks/AssistantManagerTasks';
import DailyDairyOrderReport from './DailyDairyOrderReport';
import GoodsReceivedReport from './GoodsReceivedReport';
import ProductReturnsReport from './ProductReturnsReport';
import ProduceInventoryTrackerReport from './ProduceInventoryTrackerReport';
import DailyLiquorCountReport from './DailyLiquorCountReport';
import Inventory from '../Inventory/Inventory';
import FullInventoryReport from './FullInventoryReport';
import { useAuthStore } from '../../store/authStore';

const ReportsHub = () => {
  const { hasFeatureAccess } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const reportTabs = useMemo(() => {
    const canAccessHub = hasFeatureAccess('reports.hub');
    const canViewTaskReport = canAccessHub;
    const canViewLogisticsTracker = canAccessHub && hasFeatureAccess('logistics.tracker');
    const canViewDairyReport = canAccessHub && hasFeatureAccess('dairy_order.report');
    const canViewGoodsReceivedReport = canAccessHub && hasFeatureAccess('goods_received.report');
    const canViewProductReturnsReport = canAccessHub && hasFeatureAccess('product_returns.report');
    const canViewProduceTrackerReport = canAccessHub && hasFeatureAccess('produce_tracker.report');
    const canViewDailyInventoryReport = canAccessHub && hasFeatureAccess('inventory.daily');
    const canViewFullInventoryReport = canAccessHub && hasFeatureAccess('inventory_count.report');
    const canViewDailyLiquorCountReport = canAccessHub && hasFeatureAccess('daily_liquor_count.report');

    return [
      ...(canViewTaskReport ? [{ key: 'task', label: 'Task Report', component: TaskReport }] : []),
      ...(canViewLogisticsTracker
        ? [{
            key: 'logistics-tracker',
            label: 'Logistic Tracker',
            component: () => <AssistantManagerTasks viewMode="tracker" />,
          }]
        : []),
      ...(canViewDairyReport ? [{ key: 'dairy', label: 'Dairy Order', component: DailyDairyOrderReport }] : []),
      ...(canViewGoodsReceivedReport ? [{ key: 'goods-received', label: 'Goods Received', component: GoodsReceivedReport }] : []),
      ...(canViewProductReturnsReport ? [{ key: 'product-returns', label: 'Product Returns', component: ProductReturnsReport }] : []),
      ...(canViewProduceTrackerReport ? [{ key: 'produce-tracker', label: 'Produce Tracker', component: ProduceInventoryTrackerReport }] : []),
      ...(canViewDailyInventoryReport ? [{ key: 'daily-inventory', label: 'Daily Inventory', component: Inventory }] : []),
      ...(canViewFullInventoryReport ? [{ key: 'full-inventory', label: 'Full Inventory', component: FullInventoryReport }] : []),
      ...(canViewDailyLiquorCountReport ? [{ key: 'daily-liquor-count', label: 'Daily Liquor Count', component: DailyLiquorCountReport }] : []),
    ];
  }, [hasFeatureAccess]);

  const routePreferredTab = useMemo(() => {
    const path = location.pathname;
    if (path === '/task-report') return 'task';
    if (path === '/task2') return 'logistics-tracker';
    if (path === '/dairy-order-report') return 'dairy';
    if (path === '/goods-received-report') return 'goods-received';
    if (path === '/product-returns-report') return 'product-returns';
    if (path === '/produce-inventory-tracker-report') return 'produce-tracker';
    if (path === '/full-inventory-report') return 'full-inventory';
    if (path === '/daily-liquor-count-report') return 'daily-liquor-count';
    if (path === '/inventory') return 'daily-inventory';
    return '';
  }, [location.pathname]);

  const activeTab = searchParams.get('tab') || routePreferredTab || reportTabs[0]?.key || '';

  useEffect(() => {
    if (!reportTabs.length) return;
    if (!reportTabs.some((tab) => tab.key === activeTab)) {
      setSearchParams({ tab: reportTabs[0].key }, { replace: true });
    }
  }, [activeTab, reportTabs, setSearchParams]);

  const selectedTab = reportTabs.find((tab) => tab.key === activeTab) || reportTabs[0];
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(selectedTab ? [selectedTab.key] : []));

  useEffect(() => {
    if (!selectedTab?.key) return;
    setVisitedTabs((current) => {
      if (current.has(selectedTab.key)) return current;
      const next = new Set(current);
      next.add(selectedTab.key);
      return next;
    });
  }, [selectedTab?.key]);

  if (!reportTabs.length) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {reportTabs.map((tab) => {
          const isActive = tab.key === selectedTab?.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setSearchParams({ tab: tab.key })}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                isActive
                  ? 'bg-[linear-gradient(135deg,#dbeafe_0%,#bfdbfe_38%,#ccfbf1_100%)] text-sky-900 shadow-lg shadow-sky-200/60 ring-1 ring-sky-200'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {reportTabs.map((tab) => {
        if (!visitedTabs.has(tab.key)) return null;
        const TabComponent = tab.component;
        const isActive = tab.key === selectedTab?.key;
        return (
          <div key={tab.key} className={isActive ? 'block' : 'hidden'}>
            <TabComponent />
          </div>
        );
      })}
    </div>
  );
};

export default ReportsHub;
